/**
 * STEVE INVADERS — la logique du mini-jeu, sans DOM, sans canvas, sans React.
 *
 * Tout est ici : la vague, les déplacements, les collisions, les vies, le
 * score. Le rendu ne fait que lire cet état, ce qui rend le jeu testable en
 * entier — un jeu qu'on ne peut vérifier qu'à l'œil finit toujours par avoir
 * une collision fausse dans un cas sur cent.
 *
 * Unités : des pixels logiques (192 × 144), et des millisecondes. Le tirage
 * aléatoire passe par une graine portée dans l'état : la même suite d'entrées
 * donne toujours la même partie.
 */

import { BUBBLE, FOE, HOOK, STEVE } from './sprites';

/** Résolution logique de l'écran de jeu, à l'ancienne. */
export const GAME_WIDTH = 192;
export const GAME_HEIGHT = 144;

/** Marges et pas de la formation, façon Space Invaders. */
const FOE_COLUMNS = 7;
const FOE_ROWS = 3;
const FOE_GAP_X = 5;
const FOE_GAP_Y = 4;
const FOE_STEP_X = 3;
const FOE_STEP_Y = 6;
const FOE_TOP = 22;

/** Cadence de la formation : elle accélère à mesure qu'elle se vide. */
const STEP_MS_BASE = 620;
const STEP_MS_MIN = 90;
const STEP_MS_PER_WAVE = 60;

const STEVE_SPEED = 78;
const STEVE_Y = GAME_HEIGHT - STEVE.height - 3;
const BUBBLE_SPEED = 110;
const BUBBLE_MAX = 3;
const FIRE_COOLDOWN_MS = 240;
const HOOK_SPEED = 52;
/** Probabilité qu'un pas de la formation lâche un hameçon. */
const HOOK_CHANCE = 0.22;
/** Invulnérabilité après un coup : le temps de se remettre, et de le voir. */
const HIT_FLASH_MS = 900;

export const START_LIVES = 3;
/** Points par rangée, la plus haute vaut le plus cher. */
const ROW_POINTS = [30, 20, 10] as const;
const WAVE_BONUS = 100;

export type Phase = 'ready' | 'playing' | 'over';

export interface Foe {
  readonly x: number;
  readonly y: number;
  readonly row: number;
  readonly alive: boolean;
}

export interface Mover {
  readonly x: number;
  readonly y: number;
}

/**
 * Le bilan d'une vague qui vient d'être nettoyée.
 *
 * Il n'existe que le temps d'une image, comme `hitThisFrame` : la vague
 * suivante remet aussitôt les compteurs à zéro, et sans ce bilan il n'y aurait
 * plus rien à lire au moment où l'on veut décider d'un succès.
 */
export interface WaveReport {
  /** La vague terminée, pas celle qui commence. */
  readonly wave: number;
  readonly shots: number;
  readonly hits: number;
  readonly lostLives: number;
  readonly livesLeft: number;
}

export interface GameState {
  readonly phase: Phase;
  readonly steveX: number;
  readonly lives: number;
  readonly score: number;
  readonly wave: number;
  readonly foes: readonly Foe[];
  readonly bubbles: readonly Mover[];
  readonly hooks: readonly Mover[];
  readonly direction: 1 | -1;
  readonly stepIn: number;
  readonly fireIn: number;
  readonly flashIn: number;
  readonly seed: number;
  /** Vrai le temps d'une image, pour que le rendu puisse réagir à un impact. */
  readonly hitThisFrame: boolean;
  /** Bulles crachées et poissons touchés depuis le début de la partie. */
  readonly shots: number;
  readonly kills: number;
  /** Les mêmes, mais depuis le début de la vague en cours. */
  readonly waveShots: number;
  readonly waveHits: number;
  /** Vies au moment où la vague en cours a commencé. */
  readonly waveStartLives: number;
  /** Non nul le temps d'une image, à la fin d'une vague. */
  readonly clearedWave: WaveReport | null;
}

export interface Input {
  readonly left: boolean;
  readonly right: boolean;
  readonly fire: boolean;
  /** Position visée au pointeur, en pixels logiques ; `null` au clavier. */
  readonly aimX: number | null;
}

export const NO_INPUT: Input = { left: false, right: false, fire: false, aimX: null };

/** Générateur déterministe (mulberry32) : même graine, même partie. */
function nextRandom(seed: number): { value: number; seed: number } {
  let t = (seed + 0x6d2b79f5) | 0;
  t = Math.imul(t ^ (t >>> 15), 1 | t);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return { value, seed: t | 0 };
}

/** Largeur totale d'une formation pleine. */
const FORMATION_WIDTH = FOE_COLUMNS * FOE.width + (FOE_COLUMNS - 1) * FOE_GAP_X;

function makeFoes(wave: number): Foe[] {
  const left = Math.round((GAME_WIDTH - FORMATION_WIDTH) / 2);
  // Chaque vague démarre une rangée plus bas : la pression monte.
  const top = FOE_TOP + Math.min(4, wave - 1) * 5;
  const foes: Foe[] = [];
  for (let row = 0; row < FOE_ROWS; row += 1) {
    for (let column = 0; column < FOE_COLUMNS; column += 1) {
      foes.push({
        x: left + column * (FOE.width + FOE_GAP_X),
        y: top + row * (FOE.height + FOE_GAP_Y),
        row,
        alive: true,
      });
    }
  }
  return foes;
}

export function createGame(seed = 1, phase: Phase = 'ready'): GameState {
  return {
    phase,
    steveX: Math.round((GAME_WIDTH - STEVE.width) / 2),
    lives: START_LIVES,
    score: 0,
    wave: 1,
    foes: makeFoes(1),
    bubbles: [],
    hooks: [],
    direction: 1,
    stepIn: STEP_MS_BASE,
    fireIn: 0,
    flashIn: 0,
    seed,
    hitThisFrame: false,
    shots: 0,
    kills: 0,
    waveShots: 0,
    waveHits: 0,
    waveStartLives: START_LIVES,
    clearedWave: null,
  };
}

/** Intervalle entre deux pas de la formation, selon ce qu'il en reste. */
export function stepInterval(state: GameState): number {
  const alive = state.foes.filter((foe) => foe.alive).length;
  const total = FOE_COLUMNS * FOE_ROWS;
  const progress = 1 - alive / total;
  const base = STEP_MS_BASE - (state.wave - 1) * STEP_MS_PER_WAVE;
  return Math.max(STEP_MS_MIN, Math.round(base * (1 - progress * 0.8)));
}

function overlaps(
  ax: number,
  ay: number,
  aw: number,
  ah: number,
  bx: number,
  by: number,
  bw: number,
  bh: number,
): boolean {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

/**
 * Avance la partie d'un pas de temps.
 *
 * `dt` est borné : un onglet en arrière-plan revient avec plusieurs secondes
 * d'un coup, et sans borne la formation traverserait l'écran en une image.
 */
export function stepGame(state: GameState, input: Input, dtMs: number): GameState {
  if (state.phase !== 'playing') return state;
  const dt = Math.min(50, Math.max(0, dtMs));
  const seconds = dt / 1000;
  let { seed } = state;
  const random = (): number => {
    const drawn = nextRandom(seed);
    seed = drawn.seed;
    return drawn.value;
  };

  // ── Steve ───────────────────────────────────────────────────────────────
  let steveX = state.steveX;
  if (input.aimX !== null) {
    steveX = input.aimX - STEVE.width / 2;
  } else {
    if (input.left) steveX -= STEVE_SPEED * seconds;
    if (input.right) steveX += STEVE_SPEED * seconds;
  }
  steveX = Math.max(0, Math.min(GAME_WIDTH - STEVE.width, steveX));

  // ── Bulles ──────────────────────────────────────────────────────────────
  let fireIn = Math.max(0, state.fireIn - dt);
  let shots = state.shots;
  let waveShots = state.waveShots;
  let bubbles = state.bubbles
    .map((bubble) => ({ x: bubble.x, y: bubble.y - BUBBLE_SPEED * seconds }))
    .filter((bubble) => bubble.y + BUBBLE.height > 0);

  if (input.fire && fireIn === 0 && bubbles.length < BUBBLE_MAX) {
    // La bulle sort de la bouche : à droite du sprite, à hauteur de l'œil.
    bubbles = [
      ...bubbles,
      { x: Math.round(steveX + STEVE.width * 0.72), y: STEVE_Y + 2 },
    ];
    fireIn = FIRE_COOLDOWN_MS;
    shots += 1;
    waveShots += 1;
  }

  // ── Formation ───────────────────────────────────────────────────────────
  let foes = state.foes;
  let direction = state.direction;
  let hooks = state.hooks.map((hook) => ({ x: hook.x, y: hook.y + HOOK_SPEED * seconds }));
  let stepIn = state.stepIn - dt;

  if (stepIn <= 0) {
    const alive = foes.filter((foe) => foe.alive);
    const minX = Math.min(...alive.map((foe) => foe.x));
    const maxX = Math.max(...alive.map((foe) => foe.x + FOE.width));
    const hitsEdge =
      (direction === 1 && maxX + FOE_STEP_X > GAME_WIDTH) || (direction === -1 && minX - FOE_STEP_X < 0);

    // Seuls les vivants avancent : un poisson touché reste où il est tombé, et
    // la formation ne peut donc jamais dépasser le bord par un fantôme.
    foes = foes.map((foe) => {
      if (!foe.alive) return foe;
      return hitsEdge
        ? { ...foe, y: foe.y + FOE_STEP_Y }
        : { ...foe, x: foe.x + FOE_STEP_X * direction };
    });
    if (hitsEdge) direction = direction === 1 ? -1 : 1;

    // Un poisson de la rangée du bas lâche parfois un hameçon.
    if (random() < HOOK_CHANCE && alive.length > 0) {
      const lowest = new Map<number, Foe>();
      for (const foe of foes) {
        if (!foe.alive) continue;
        const previous = lowest.get(foe.x);
        if (!previous || foe.y > previous.y) lowest.set(foe.x, foe);
      }
      const candidates = [...lowest.values()];
      const chosen = candidates[Math.floor(random() * candidates.length)];
      if (chosen) {
        hooks = [
          ...hooks,
          { x: chosen.x + Math.floor(FOE.width / 2) - 1, y: chosen.y + FOE.height },
        ];
      }
    }
    stepIn = stepInterval({ ...state, foes, wave: state.wave });
  }

  // ── Collisions : bulles contre poissons ─────────────────────────────────
  let score = state.score;
  let kills = state.kills;
  let waveHits = state.waveHits;
  // Une bulle ne touche qu'un poisson : elle est retirée dès qu'elle a servi.
  const spent = new Set<number>();
  foes = foes.map((foe) => {
    if (!foe.alive) return foe;
    for (const [index, bubble] of bubbles.entries()) {
      if (spent.has(index)) continue;
      if (
        !overlaps(bubble.x, bubble.y, BUBBLE.width, BUBBLE.height, foe.x, foe.y, FOE.width, FOE.height)
      ) {
        continue;
      }
      spent.add(index);
      score += ROW_POINTS[foe.row] ?? 10;
      kills += 1;
      waveHits += 1;
      return { ...foe, alive: false };
    }
    return foe;
  });
  bubbles = bubbles.filter((_, index) => !spent.has(index));

  // ── Collisions : hameçons contre Steve ──────────────────────────────────
  let lives = state.lives;
  let flashIn = Math.max(0, state.flashIn - dt);
  let hitThisFrame = false;
  const remaining: Mover[] = [];
  for (const hook of hooks) {
    const touching = overlaps(
      hook.x,
      hook.y,
      HOOK.width,
      HOOK.height,
      steveX,
      STEVE_Y,
      STEVE.width,
      STEVE.height,
    );
    if (touching && flashIn === 0) {
      lives -= 1;
      flashIn = HIT_FLASH_MS;
      hitThisFrame = true;
      continue;
    }
    if (hook.y < GAME_HEIGHT) remaining.push(hook);
  }
  hooks = remaining;

  // ── Fin de vague, fin de partie ─────────────────────────────────────────
  let { wave } = state;
  let phase: Phase = state.phase;
  let waveStartLives = state.waveStartLives;
  let clearedWave: WaveReport | null = null;
  const stillAlive = foes.filter((foe) => foe.alive);

  if (stillAlive.length === 0) {
    /*
     * Le bilan est pris ici, avant la remise à zéro : c'est le seul instant où
     * l'on sait encore ce que la vague a coûté. Il porte le numéro de la vague
     * terminée, pas celui de la suivante — « terminer la vague 4 » se lit donc
     * `clearedWave.wave === 4`.
     */
    clearedWave = {
      wave,
      shots: waveShots,
      hits: waveHits,
      lostLives: Math.max(0, waveStartLives - lives),
      livesLeft: lives,
    };
    wave += 1;
    score += WAVE_BONUS;
    foes = makeFoes(wave);
    bubbles = [];
    hooks = [];
    direction = 1;
    stepIn = STEP_MS_BASE - (wave - 1) * STEP_MS_PER_WAVE;
    waveShots = 0;
    waveHits = 0;
    waveStartLives = lives;
  } else if (stillAlive.some((foe) => foe.y + FOE.height >= STEVE_Y)) {
    // Ils sont passés : la partie s'arrête, quelles que soient les vies.
    lives = 0;
    phase = 'over';
  }

  if (lives <= 0) phase = 'over';

  return {
    phase,
    steveX,
    lives: Math.max(0, lives),
    score,
    wave,
    foes,
    bubbles,
    hooks,
    direction,
    stepIn,
    fireIn,
    flashIn,
    seed,
    hitThisFrame,
    shots,
    kills,
    waveShots,
    waveHits,
    waveStartLives,
    clearedWave,
  };
}

/** Position verticale de Steve, utile au rendu. */
export const STEVE_LINE = STEVE_Y;
